import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import { findBlockLocation, IPageDocument } from 'src/helpers/pageDocumentUtils';
import {
  isSignupProtectedBlock,
  signupLegacyAdminLabelKey,
  signupTextPresentationForLegacyKey,
} from 'src/helpers/signupPageLayoutUtils';
import { IColumnsBlock, IPageBlock, ITextBlock } from 'src/interfaces/PageBlock';
import { ITrackMap } from 'src/interfaces/TrackMap';
import BlockCardToolbar from 'src/pages/Dashboard/System/Pages/components/BlockCardToolbar';
import ColumnsLayoutConfigButton from 'src/pages/Dashboard/System/Pages/components/ColumnsLayoutConfigButton';
import ColumnsLayoutEditor from 'src/pages/Dashboard/System/Pages/components/ColumnsLayoutEditor';
import PageBlockFieldsPanel from 'src/pages/Dashboard/System/Pages/components/PageBlockFieldsPanel';

interface SortableBlockCardProps {
  block: IPageBlock;
  index: number;
  containerBlockCount: number;
  document: IPageDocument;
  contentKey: string;
  pageContentId?: string;
  selectedEditorLang: LanguageKeys;
  setSelectedEditorLang: (lang: LanguageKeys) => void;
  trackMaps: ITrackMap[];
  hasSponsorsBlock: boolean;
  onUpdate: (id: string, patch: Partial<IPageBlock>) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onColumnsChange: (columnsBlockId: string, patch: Partial<IPageBlock>) => void;
  onDocumentChange: (doc: IPageDocument) => void;
  isSignupShell?: boolean;
  nested?: boolean;
}

export default function SortableBlockCard({
  block,
  index,
  containerBlockCount,
  document,
  contentKey,
  pageContentId,
  selectedEditorLang,
  setSelectedEditorLang,
  trackMaps,
  hasSponsorsBlock,
  onUpdate,
  onRemove,
  onMove,
  onColumnsChange,
  onDocumentChange,
  isSignupShell = false,
  nested = false,
}: SortableBlockCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });

  const style = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.35 : 1,
  };

  const loc = findBlockLocation(document, block.id);
  const canMoveUp = loc ? loc.index > 0 : false;
  const canMoveDown = loc ? loc.index < containerBlockCount - 1 : false;
  const showVisibilityRules = block.type !== 'results';
  const canRemove = !isSignupShell || !isSignupProtectedBlock(block);
  const textBlock = block.type === 'text' ? (block as ITextBlock) : undefined;
  const textPresentation =
    textBlock?.textPresentation ?? signupTextPresentationForLegacyKey(textBlock?.legacyKey);

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{ p: nested ? 1.5 : 2, mb: nested ? 1 : 2 }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: expanded ? 1 : 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            {...attributes}
            {...listeners}
            sx={{
              display: 'flex',
              cursor: 'grab',
              touchAction: 'none',
              color: 'text.secondary',
              flexShrink: 0,
            }}
            aria-label={t('system.pages.dragBlock')}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
          <IconButton
            size="small"
            onClick={() => setExpanded(prev => !prev)}
            aria-label={expanded ? t('system.pages.collapseBlock') : t('system.pages.expandBlock')}
            aria-expanded={expanded}
            sx={{ flexShrink: 0 }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <Button
            variant="text"
            size="small"
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: theme => theme.typography.subtitle2.fontSize,
              lineHeight: theme => theme.typography.subtitle2.lineHeight,
              p: 0,
              minWidth: 0,
              justifyContent: 'flex-start',
              flex: 1,
            }}
          >
            {t(`system.pages.blockTypes.${block.type}`)} #{index + 1}
          </Button>
          {isSignupShell && textBlock?.legacyKey ? (
            <Chip
              size="small"
              label={
                (() => {
                  const labelKey = signupLegacyAdminLabelKey(textBlock.legacyKey);
                  return labelKey ? t(labelKey) : textBlock.legacyKey;
                })()
              }
              sx={{ ml: 1 }}
            />
          ) : null}
          {isSignupShell && textBlock && textPresentation !== 'inline' ? (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label={t(`system.pages.textPresentation.${textPresentation}`)}
              sx={{ ml: 0.5 }}
            />
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {block.type === 'columns' ? (
            <ColumnsLayoutConfigButton
              layout={block as IColumnsBlock}
              onColumnsPatch={patch => onColumnsChange(block.id, patch)}
            />
          ) : null}
          <BlockCardToolbar
            showFrostedBackground={block.type === 'text'}
            frostedBackground={block.type === 'text' ? (block as ITextBlock).frostedBackground : false}
            onFrostedBackgroundChange={
              block.type === 'text'
                ? enabled =>
                    onUpdate(block.id, { frostedBackground: enabled } as Partial<ITextBlock>)
                : undefined
            }
            showVisibilityRules={showVisibilityRules}
            visibility={block.visibility}
            onVisibilityChange={v => onUpdate(block.id, { visibility: v })}
            spacing={block.spacing}
            onSpacingChange={v => onUpdate(block.id, { spacing: v })}
            isDividerBlock={block.type === 'divider'}
            showSignupPresets={isSignupShell && showVisibilityRules}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
            onMoveUp={() => onMove(block.id, 'up')}
            onMoveDown={() => onMove(block.id, 'down')}
            onRemove={() => onRemove(block.id)}
            canRemove={canRemove}
          />
        </Box>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Box>
          {block.type === 'results' ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('system.pages.resultsBlockNoVisibility')}
            </Typography>
          ) : null}
          {block.type === 'columns' ? (
            <ColumnsLayoutEditor
              layout={block}
              document={document}
              onDocumentChange={onDocumentChange}
              onColumnsPatch={patch => onColumnsChange(block.id, patch)}
              selectedEditorLang={selectedEditorLang}
              setSelectedEditorLang={setSelectedEditorLang}
              contentKey={contentKey}
              pageContentId={pageContentId}
              trackMaps={trackMaps}
              hasSponsorsBlockInPage={hasSponsorsBlock}
              isSignupShell={isSignupShell}
            />
          ) : block.type === 'signupFlow' ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('system.pages.signupFlowBlockHint')}
            </Typography>
          ) : (
            <PageBlockFieldsPanel
              block={block}
              contentKey={contentKey}
              pageContentId={pageContentId}
              selectedEditorLang={selectedEditorLang}
              setSelectedEditorLang={setSelectedEditorLang}
              trackMaps={trackMaps}
              isSignupShell={isSignupShell}
              onUpdate={onUpdate}
            />
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
